import {
  Component,
  EventEmitter,
  Input,
  inject,
  input,
  type OnChanges,
  type OnInit,
  Output,
  type SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { EnvironmentService } from '@core/environment/environment.service';
import { FinoButtonModule } from '@fino-ui/button';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule } from '@fino-ui/translation';

@Component({
  selector: 'app-iban-check',
  templateUrl: './iban-check.component.html',
  styleUrls: ['./iban-check.component.sass'],
  imports: [
    FinoTranslationModule,
    FormsModule,
    ReactiveFormsModule,
    FinoInputModule,
    FinoButtonModule,
  ],
})
export class IbanCheckComponent implements OnInit, OnChanges {
  private readonly envService = inject(EnvironmentService);

  public ibanForm: SubmittableForm = new SubmittableForm({
    newIbanStart: new FormControl('', Validators.required),
    newIbanEnd: new FormControl('', [Validators.required]),
    oldIbanEnd: new FormControl('', [Validators.required]),
  });

  newIbanStartValid = false;
  newIbanEndValid = false;
  oldIbanEndValid = false;

  public readonly id = input<string>('');

  public readonly previousJobId = input<string>('');

  public readonly newIban = input<string>('');

  public readonly oldIban = input<string>('');

  public readonly isQualityAssurance = input<boolean>(false);

  @Output('checkSuccess')
  public checkSuccess = new EventEmitter();

  get isAustrianIban() {
    return this.newIban()?.toUpperCase().startsWith('AT');
  }

  ngOnChanges(_changes: SimpleChanges): void {
    // in austria, lists dont have new ibans printed, skip check
    this.newIbanEndValid = this.isAustrianIban;
    this.newIbanStartValid = this.isAustrianIban;
  }

  get checkOldIbanAsWell() {
    // in austria, lists dont have new ibans printed therefore validate against old iban
    return !this.isQualityAssurance() || this.isAustrianIban;
  }

  notifyAboutCheck() {
    let success = this.newIbanStartValid && this.newIbanEndValid;

    if (this.checkOldIbanAsWell) {
      success = success && this.oldIbanEndValid;
    }

    if (success) {
      this.ibanForm.reset();
    }
    this.checkSuccess.emit(success);
  }

  get showDemoButton() {
    return !this.envService.isProduction;
  }

  skipCheck() {
    this.newIbanStartValid = true;
    this.newIbanEndValid = true;
    this.oldIbanEndValid = true;
    this.notifyAboutCheck();
  }

  ngOnInit() {
    this.ibanForm.controls.newIbanStart?.valueChanges.subscribe((v) => {
      const { newIban: newIbanInput } = this;
      const newIban = newIbanInput();
      if (!newIban) return;

      const newIbanStart = newIban.slice(2, 4);
      this.newIbanStartValid = newIbanStart === v;

      if (!this.newIbanStartValid) {
        this.ibanForm.controls.newIbanStart.setErrors({
          newIbanStart: true,
        });
      }
      this.notifyAboutCheck();
    });

    this.ibanForm.controls.newIbanEnd?.valueChanges.subscribe((v) => {
      const { newIban: newIbanInput } = this;
      const newIban = newIbanInput();
      if (!newIban) return;

      this.newIbanEndValid = newIban.slice(-6) === v;
      if (!this.newIbanEndValid) {
        this.ibanForm.controls.newIbanEnd.setErrors({
          newIbanEnd: true,
        });
      }
      this.notifyAboutCheck();
    });

    this.ibanForm.controls.oldIbanEnd?.valueChanges.subscribe((v) => {
      const { oldIban: oldIbanInput } = this;
      const oldIban = oldIbanInput();
      if (!oldIban) return;

      const lastTenDigits = oldIban.slice(-10);
      const regex = new RegExp(`^\\d{0,4}${v}$`, 'gm');
      this.oldIbanEndValid = regex.test(lastTenDigits);
      if (!this.oldIbanEndValid) {
        this.ibanForm.controls.oldIbanEnd.setErrors({
          oldIbanEnd: true,
        });
      }
      this.notifyAboutCheck();
    });
  }
}
